import lottie from 'lottie-web';

let observer;
let pageReadyObserver;
let isPageReady = false;

/**
 * Attempts to load the Lottie animation for an element if conditions are met.
 * @param {HTMLElement} element 
 */
const attemptLoad = (element) => {
    // Check if already loaded to avoid duplicates
    if (element.classList.contains('lottie-loaded')) return;
    
    // Check conditions: Must be in view AND page must be ready
    const inView = element.dataset.inView === 'true';
    const ready = document.documentElement.classList.contains('page-ready');
    
    if (inView && ready) {
        loadAnimation(element);
    }
};

/**
 * Loads the Lottie animation.
 * @param {HTMLElement} element 
 */
const loadAnimation = (element) => {
    element.classList.add('lottie-loaded');
    
    // Data attributes
    const path = element.dataset.lottiePath;
    const delay = parseInt(element.dataset.lottieDelay || 0, 10);
    const loop = element.dataset.lottieLoop !== 'false'; // Default to true
    const autoplay = element.dataset.lottieAutoplay !== 'false'; // Default to true

    if (!path) {
        console.warn('Lottie Loader: No path provided for element', element);
        return;
    }

    const load = () => {
         // Determine animation source: path (URL) or animationData (JSON object)
         // For now, we assume path based on user request.
         lottie.loadAnimation({
            container: element,
            renderer: 'svg',
            loop: loop,
            autoplay: autoplay,
            path: path
        });
        
        // Use the observer from the outer scope if available, or just unobserve if we can access the observer instance specific to this element.
        // Since we are using a shared observer, we can't easily access 'observer' here if it wasn't global.
        // But 'observer' IS global in this module scope.
        if (observer) {
            observer.unobserve(element);
        }
    };

    if (delay > 0) {
        setTimeout(load, delay);
    } else {
        load();
    }
};

/**
 * Initializes Lottie animations for a specific container.
 * @param {HTMLElement} container 
 */
export function initializeLottie(container = document) {
    // 1. Update local state
    isPageReady = document.documentElement.classList.contains('page-ready');

    // 2. Setup Page Ready Observer (Singleton)
    if (!pageReadyObserver) {
        pageReadyObserver = new MutationObserver((mutations) => {
             mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    const isNowReady = document.documentElement.classList.contains('page-ready');
                    
                    // If state changed to ready
                    if (isNowReady && !isPageReady) {
                        isPageReady = true;
                        // Trigger check on all pending elements in the document
                        document.querySelectorAll('[data-lottie-path]:not(.lottie-loaded)').forEach(attemptLoad);
                    } else if (!isNowReady) {
                        isPageReady = false;
                    }
                }
             });
        });
        pageReadyObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }

    // 3. Find elements in the new container
    const elements = container.querySelectorAll('[data-lottie-path]');
    if (elements.length === 0) {
        // console.warn('Lottie Loader: No elements found in container', container);
        return;
    }

    // 4. Setup Intersection Observer (Singleton/Shared)
    if (!observer) {
        observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const el = entry.target;
                // console.log('Lottie Loader: Intersection Change', { el, isIntersecting: entry.isIntersecting });
                if (entry.isIntersecting) {
                    el.dataset.inView = 'true';
                    attemptLoad(el);
                } else {
                    el.dataset.inView = 'false';
                }
            });
        }, { 
            rootMargin: '0px 0px 0px 0px', // Relaxed margin to ensure detection
            threshold: 0 
        }); 
    }
    
    elements.forEach(el => {
        el.dataset.inView = 'false'; // Initialize
        observer.observe(el);
    });
}

/**
 * Destroys all Lottie animations.
 */
export function destroyLottie() {
    lottie.destroy();
    // We don't necessarily need to disconnect observers because they are useful for the next page lifecycle if we keep instances,
    // but in a Barba context, existing elements are removed from DOM, so IntersectionObserver cleans up naturally?
    // Actually, elements removed from DOM are not GC'd if Observer holds reference?
    // IntersectionObserver holds weak references? No.
    // It is good practice to unobserve if we know elements are gone.
    // But since we can't easily track which elements are gone vs new, and we want a persistent observer...
    // Actually, 'lottie.destroy()' just stops rendering.
    // References in 'observer' might persist.
    // Ideally we should unobserve all. 
    // But let's keep it simple. The memory leak is minimal for a few lotties.
}